import { useMemo } from 'react'

import {
  Link,
  LinkProps,
  useLocation,
  useRouter,
  useRouterState,
} from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'

import { useAuth } from '@/services/auth/use-auth'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar'

interface NavLinkItem {
  label: string
  to: LinkProps['to']
  isActive?: boolean
  items?: NavLinkItem[]
}

export function AdminSidebar() {
  const nav = useNavConfig()

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="mx-auto py-2">
          <img src="/brand-yes.png" alt="logo" className="h-9" />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu className="space-y-1">
            {nav.map((item) => {
              const isDropdown = Array.isArray(item.items)

              if (!isDropdown)
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      asChild
                      isActive={item.isActive}
                      className="data-[active=true]:bg-primary-yes data-[active=true]:text-white"
                    >
                      <Link to={item.to}>{item.label}</Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )

              const defaultOpen =
                isDropdown && item.items?.some((i) => i.isActive)

              return (
                <Collapsible
                  key={item.to}
                  defaultOpen={defaultOpen}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton className="font-semibold">
                        {item.label}&nbsp;
                        <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>

                    {item.items?.length ? (
                      <CollapsibleContent>
                        <SidebarMenuSub className="space-y-1">
                          {item.items.map((item) => (
                            <SidebarMenuSubItem key={item.to}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={item.isActive}
                              >
                                <Link to={item.to}>{item.label}</Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    ) : null}
                  </SidebarMenuItem>
                </Collapsible>
              )
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="py-4">
        <p className="text-xs text-muted-foreground">
          © YES Academy 2024, Version 1.0
        </p>
      </SidebarFooter>
    </Sidebar>
  )
}

function useNavConfig() {
  const { pathname } = useLocation()
  const auth = useAuth()

  return useMemo((): NavLinkItem[] => {
    if (!auth.isAdmin) return []

    return [
      {
        label: 'Daftar Peserta',
        to: '/admin-v2/participant',
        isActive: pathname.startsWith('/admin-v2/participant'),
      },
      {
        label: 'Master Data',
        to: '/admin-v2/master/company',
        isActive: pathname.startsWith('/admin-v2/master/company'),
        items: [
          {
            label: 'Master Perusahaan',
            to: '/admin-v2/master/company',
            isActive: pathname.startsWith('/admin-v2/master/company'),
          },
          {
            label: 'Master Tag',
            to: '/admin-v2/master/tag',
            isActive: pathname.startsWith('/admin-v2/master/tag'),
          },
          {
            label: 'Master Skill',
            to: '/admin-v2/master/skill',
            isActive: pathname.startsWith('/admin-v2/master/skill'),
          },
          {
            label: 'Master Kategori',
            to: '/admin-v2/master/category',
            isActive: pathname.startsWith('/admin-v2/master/category'),
          },
          {
            label: 'Master Skema',
            to: '/admin-v2/master/schema',
            isActive: pathname.startsWith('/admin-v2/master/schema'),
          },
        ],
      },
      {
        label: 'Job Listing',
        to: '/admin-v2/job/vacancy',
        items: [
          {
            label: 'List lowongan',
            to: '/admin-v2/job/vacancy',
            isActive: pathname.startsWith('/admin-v2/job/vacancy'),
          },
          // {
          //   label: 'List pelamar',
          //   to: '/admin-v2/job/applicant',
          //   isActive: pathname.startsWith('/admin-v2/job/applicant'),
          // },
          {
            label: 'List pelamar',
            to: '/admin-v2/job/applicant/vacancy',
            isActive: pathname.startsWith('/admin-v2/job/applicant'),
          },
        ],
      },
      {
        label: 'Referrer',
        to: '/admin-v2/referral',
        isActive: pathname.startsWith('/admin-v2/referral'),
        items: [
          {
            label: 'Daftar Referrer',
            to: '/admin-v2/referral',
            isActive: pathname === '/admin-v2/referral',
          },
          {
            label: 'Riwayat Tukar Poin',
            to: '/admin-v2/referral/point-history',
            isActive: pathname === '/admin-v2/referral/point-history',
          },
        ],
      },
    ]
  }, [pathname, auth.isAdmin])
}
